import { create } from 'zustand';
import { api } from './api';
import type { Entity, FlowNode, Allocation, DailyMap, Side, EntityType } from './types';

const DEFAULT_DATE = '1405/06/05';

function loadLocal<T>(key: string, fallback: T): T {
  const v = localStorage.getItem(key);
  return v === null ? fallback : (JSON.parse(v) as T);
}
function saveLocal(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

interface StoreState {
  entities: Entity[];
  dailyMap: DailyMap | null;
  nodes: FlowNode[];
  allocations: Allocation[];
  date: string;
  lang: 'en' | 'fa';
  theme: 'light' | 'dark';
  currency: string;
  zoom: number;
  panX: number;
  panY: number;
  loading: boolean;
  error: string | null;

  bootstrap: () => Promise<void>;
  setDate: (d: string) => Promise<void>;
  refreshDay: () => Promise<void>;

  addEntity: (type: EntityType, name: string) => Promise<void>;
  removeEntity: (id: string) => Promise<void>;

  addNode: (side: Side, entityId: string, totalAmount: number, subLabel?: string) => Promise<void>;
  updateNodeData: (id: string, totalAmount: number, subLabel?: string) => Promise<void>;
  removeNode: (id: string) => Promise<void>;

  addAllocation: (sourceNodeId: string, targetNodeId: string, amount: number, note?: string) => Promise<void>;
  removeAllocation: (id: string) => Promise<void>;

  setLang: (l: 'en' | 'fa') => void;
  setTheme: (t: 'light' | 'dark') => void;
  setCurrency: (c: string) => void;

  setView: (zoom: number, panX: number, panY: number) => void;
}

export const useStore = create<StoreState>((set, get) => ({
  entities: [],
  dailyMap: null,
  nodes: [],
  allocations: [],
  date: loadLocal<string>('coco_date', DEFAULT_DATE),
  lang: loadLocal<'en' | 'fa'>('coco_lang', 'en'),
  theme: loadLocal<'light' | 'dark'>('coco_theme', 'light'),
  currency: loadLocal<string>('coco_currency', '$'),
  zoom: 1,
  panX: 0,
  panY: 0,
  loading: false,
  error: null,

  async bootstrap() {
    set({ loading: true, error: null });
    try {
      const [settings, entities] = await Promise.all([api.getSettings(), api.getEntities()]);
      if (!loadLocal('coco_lang', null)) set({ lang: settings.lang });
      if (!loadLocal('coco_theme', null)) set({ theme: settings.theme });
      if (!loadLocal('coco_currency', null)) set({ currency: settings.currency });
      set({ entities });
      await get().refreshDay();
    } catch (e) {
      set({ error: (e as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  async setDate(d: string) {
    saveLocal('coco_date', d);
    set({ date: d });
    await get().refreshDay();
  },

  async refreshDay() {
    const { date } = get();
    set({ loading: true, error: null });
    try {
      const dailyMap = await api.getOrCreateDailyMap(date);
      const [nodes, allocations] = await Promise.all([api.getNodes(dailyMap.id), api.getAllocations(dailyMap.id)]);
      set({ dailyMap, nodes, allocations });
    } catch (e) {
      set({ error: (e as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  async addEntity(type, name) {
    const entity = await api.createEntity(type, name);
    set({ entities: [...get().entities, entity] });
  },
  async removeEntity(id) {
    await api.deleteEntity(id);
    set({ entities: get().entities.filter((e) => e.id !== id) });
  },

  async addNode(side, entityId, totalAmount, subLabel) {
    const dm = get().dailyMap;
    if (!dm) throw new Error('No daily map');
    const node = await api.createNode({
      dailyMapId: dm.id,
      side,
      entityId,
      totalAmount,
      metadataJson: subLabel ? JSON.stringify({ subLabel }) : null,
    });
    set((st) => {
      const idx = st.nodes.findIndex((n) => n.id === node.id);
      const nodes = idx >= 0 ? st.nodes.map((n) => (n.id === node.id ? node : n)) : [...st.nodes, node];
      return { nodes };
    });
  },
  async updateNodeData(id, totalAmount, subLabel) {
    const node = await api.updateNode(id, { totalAmount, metadataJson: subLabel ? JSON.stringify({ subLabel }) : null });
    set({ nodes: get().nodes.map((n) => (n.id === id ? node : n)) });
  },
  async removeNode(id) {
    await api.deleteNode(id);
    set({ nodes: get().nodes.filter((n) => n.id !== id), allocations: get().allocations.filter((a) => a.sourceNodeId !== id && a.targetNodeId !== id) });
  },

  async addAllocation(sourceNodeId, targetNodeId, amount, note) {
    const dm = get().dailyMap;
    if (!dm) throw new Error('No daily map');
    const allocation = await api.createAllocation({ dailyMapId: dm.id, sourceNodeId, targetNodeId, amount, note });
    set({ allocations: [...get().allocations, allocation] });
  },
  async removeAllocation(id) {
    await api.deleteAllocation(id);
    set({ allocations: get().allocations.filter((a) => a.id !== id) });
  },

  setLang(l) {
    saveLocal('coco_lang', l);
    set({ lang: l });
    api.updateSettings({ lang: l }).catch(() => {});
  },
  setTheme(t) {
    saveLocal('coco_theme', t);
    set({ theme: t });
    api.updateSettings({ theme: t }).catch(() => {});
  },
  setCurrency(c) {
    saveLocal('coco_currency', c);
    set({ currency: c });
    api.updateSettings({ currency: c }).catch(() => {});
  },

  setView(zoom, panX, panY) {
    set({ zoom, panX, panY });
  },
}));
